import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import SafeIcon from '../common/SafeIcon';
import * as LuIcons from 'react-icons/lu';

const { LuPlus, LuMinus, LuCircleHelp } = LuIcons;

const FAQItem = ({ question, answer, isOpen, toggle }) => {
  return (
    <div className={`faq-item ${isOpen ? 'open' : ''}`}>
      <button className="faq-question" onClick={toggle}>
        <span>{question}</span>
        <div className="icon-wrapper">
          <SafeIcon icon={isOpen ? LuMinus : LuPlus} />
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="faq-answer-wrapper"
          >
            <div className="faq-answer">
              <p>{answer}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <style jsx>{`
        .faq-item {
          background: rgba(221, 219, 230, 0.03);
          border: 1px solid rgba(221, 219, 230, 0.08);
          border-radius: 16px;
          margin-bottom: 1rem;
          overflow: hidden;
          transition: 0.3s;
        }
        .faq-item.open {
          background: rgba(131, 119, 190, 0.05);
          border-color: rgba(131, 119, 190, 0.3);
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
        }
        .faq-question {
          width: 100%;
          padding: 1.5rem 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: none;
          border: none;
          color: white;
          font-size: 1.1rem;
          font-weight: 600;
          text-align: left;
          cursor: pointer;
        }
        .icon-wrapper {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: rgba(131, 119, 190, 0.1);
          color: #8377BE;
          transition: 0.3s;
        }
        .faq-item.open .icon-wrapper {
          background: #8377BE;
          color: white;
          transform: rotate(180deg);
        }
        .faq-answer {
          padding: 0 2rem 2rem;
          color: #848489;
          line-height: 1.6;
          font-size: 1rem;
        }
        @media (max-width: 480px) {
          .faq-question { padding: 1rem 1.25rem; font-size: 1rem; }
          .faq-answer { padding: 0 1.25rem 1.25rem; font-size: 0.95rem; }
        }
      `}</style>
    </div>
  );
};

const FAQSection = () => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState('all');

  const questions = t('faq.questions', { returnObjects: true });
  const categories = ['all', 'general', 'marine', 'booking'];

  const filteredQuestions = activeCategory === 'all' 
    ? questions 
    : questions.filter(q => q.category === activeCategory);

  return (
    <section id="faq" className="section-container">
      <div className="section-header text-center mb-16">
        <h2 className="section-title">{t('faq.title')}</h2>
        <p className="section-subtitle max-w-2xl mx-auto">{t('faq.subtitle')}</p>
      </div>

      <div className="faq-layout max-w-4xl mx-auto">
        <div className="category-filter mb-12 flex flex-wrap justify-center gap-3">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-6 py-2.5 rounded-xl font-semibold transition-all ${
                activeCategory === cat 
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' 
                : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/5'
              }`}
            >
              {cat === 'all' ? t(`faq.categories.all`) : t(`faq.categories.${cat}`)}
            </button>
          ))}
        </div>

        <div className="faq-list">
          <AnimatePresence mode="popLayout">
            {filteredQuestions.map((item, index) => (
              <motion.div
                key={item.q}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
              >
                <FAQItem
                  question={item.q}
                  answer={item.a}
                  isOpen={openIndex === index}
                  toggle={() => setOpenIndex(openIndex === index ? -1 : index)}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      <style jsx>{`
        .section-title { font-size: 3rem; font-weight: 800; margin-bottom: 1rem; }
        .section-subtitle { color: #848489; font-size: 1.1rem; }

        @media (max-width: 768px) {
          .section-title { font-size: 2.2rem; }
          .section-subtitle { font-size: 1rem; }
        }

        @media (max-width: 480px) {
          .section-title { font-size: 1.9rem; }
          .section-subtitle { font-size: 0.9rem; }
        }
      `}</style>
    </section>
  );
};

export default FAQSection;